import * as React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  Button,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";

type ButtonIconStart = React.ComponentProps<typeof Button>["iconStart"];

/**
 * Opportunities bar is a short stack. 9rem clears it on a phone without the
 * empty band a scripts-sized pad leaves under a ~110px bar.
 */
export const HUB_OPPORTUNITY_SELECTION_BAR_SCROLL_PADDING =
  "pb-[calc(9rem+env(safe-area-inset-bottom))] sm:pb-24";

/**
 * Scripts & Briefs bar stacks Delete, the 44px Export control, both AI
 * actions, and Send, plus the selection row, card padding, a 3-line cap
 * warning, and the safe-area inset. 23rem covers that stack. From sm the
 * actions still wrap; one row plus the warning fits again at lg.
 */
export const HUB_SCRIPT_SELECTION_BAR_SCROLL_PADDING =
  "pb-[calc(23rem+env(safe-area-inset-bottom))] sm:pb-56 lg:pb-24";

interface HubBarButtonAction {
  kind?: "button";
  id: string;
  label: string;
  icon?: ButtonIconStart;
  variant?: React.ComponentProps<typeof Button>["variant"];
  disabled?: boolean;
  busy?: boolean;
  /** Explains a disabled state; rendered as a tooltip on a wrapper so it still opens. */
  tooltip?: string;
  className?: string;
  testId?: string;
  onClick: () => void;
}

/** A control the bar cannot express as a single click, such as the export format menu. */
interface HubBarCustomAction {
  kind: "custom";
  id: string;
  node: React.ReactNode;
}

export type HubBarAction = HubBarButtonAction | HubBarCustomAction;

export interface OpportunityHubActionBarProps {
  selectedCount: number;
  selectedLabel: string;
  onClearSelection: () => void;
  actions: HubBarAction[];
  warning?: string | null;
  ariaLabel: string;
  testId?: string;
  /**
   * When false, this bar does not repeat the selection count. The opportunities
   * breadcrumb pill is the only copy of that count.
   */
  showSelectionCopy?: boolean;
}

/**
 * Selection fly-in shared by every hub tab, so the opportunities and scripts
 * bars are the same control with different actions. Styling follows the legacy
 * Opportunities `BulkActionBar` pill.
 */
export function OpportunityHubActionBar({
  selectedCount,
  selectedLabel,
  onClearSelection,
  actions,
  warning,
  ariaLabel,
  testId,
  showSelectionCopy = true,
}: OpportunityHubActionBarProps) {
  const { t } = useTranslation();

  if (selectedCount <= 0) return null;

  const bar = (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-3 pb-[max(1rem,env(safe-area-inset-bottom))] animate-in fade-in slide-in-from-bottom-2 duration-300 sm:bottom-4 sm:px-4 sm:pb-0"
      role="region"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <div className="pointer-events-auto flex w-full max-w-lg flex-col gap-2 rounded-md border border-border bg-background px-3 py-3 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_-1px_rgba(0,0,0,0.1)] sm:w-auto sm:max-w-3xl sm:px-4 sm:py-2 dark:bg-muted">
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-0">
          <div className="flex w-full min-w-0 items-center justify-between gap-2 sm:w-auto sm:justify-start">
            {showSelectionCopy ? (
              <div className="flex min-w-0 items-center gap-2">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-medium tabular-nums text-primary-foreground">
                  {selectedCount}
                </span>
                <span className="truncate text-sm font-medium text-foreground">{selectedLabel}</span>
              </div>
            ) : null}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onClearSelection}
                    className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label={t("personaOpportunities.hub.bulk.clearSelection", "Clear selection")}
                  >
                    <Icon name="x" size="xs" color="inherit" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {t("personaOpportunities.hub.bulk.clearSelection", "Clear selection")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="grid w-full gap-2 sm:flex sm:w-auto sm:items-center sm:gap-1.5">
            {actions.map((action) => (
              <BarAction key={action.id} action={action} />
            ))}
          </div>
        </div>

        {warning ? (
          <p
            role="status"
            className="text-xs text-muted-foreground"
            data-testid="opportunity-hub-bulk-warning"
          >
            {warning}
          </p>
        ) : null}
      </div>
    </div>
  );

  return createPortal(bar, document.body);
}

function BarAction({ action }: { action: HubBarAction }) {
  if (action.kind === "custom") return action.node;

  const disabled = action.disabled || action.busy;
  const button = (
    <Button
      type="button"
      size="sm"
      variant={action.variant}
      disabled={disabled}
      aria-busy={action.busy || undefined}
      aria-label={action.tooltip ? `${action.label}. ${action.tooltip}` : undefined}
      iconStart={action.busy ? undefined : action.icon}
      onClick={action.onClick}
      data-testid={action.testId}
      className={cn("min-h-9 w-full text-xs sm:min-h-0 sm:w-auto", action.className)}
    >
      {action.busy ? (
        <Icon
          name="loader"
          size="xs"
          color="inherit"
          className="shrink-0 animate-spin motion-reduce:animate-none"
          aria-hidden
        />
      ) : null}
      <span className="truncate">{action.label}</span>
    </Button>
  );

  if (!action.tooltip) return button;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex w-full sm:w-auto">{button}</span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">{action.tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
